<template>
  <div class="calendar-page" :style="{ backgroundImage: `url(${designUrl}CalendarPage.jpeg)` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('UsersFavouritesPage')">My Favourites</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">My Calendar</h1>
      <p class="page-sub">Track your upcoming and past meetings</p>
    </div>

    <div class="page-wrapper">
      <div class="controls-row">
        <div class="tab-group">
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'upcoming' }" @click="activeTab = 'upcoming'">Upcoming</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'past' }" @click="activeTab = 'past'">Past</button>
          <template v-if="userType === 'Pet Lover'">
            <button class="tab-btn" :class="{ 'tab-active': activeTab === 'accepted' }" @click="activeTab = 'accepted'">Accepted</button>
            <button class="tab-btn" :class="{ 'tab-active': activeTab === 'denied' }" @click="activeTab = 'denied'">Denied</button>
          </template>
        </div>

        <button class="sync-btn" @click="syncToGoogle" :disabled="syncing">
          <svg class="sync-icon" :class="{ spinning: syncing }" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          {{ syncing ? 'Syncing…' : 'Sync' }}
        </button>
      </div>

      <div v-if="filteredMeetings.length === 0" class="empty-state">
        <div class="empty-icon">📅</div>
        <p class="empty-text">No meetings here</p>
        <p class="empty-sub">{{
          activeTab === 'upcoming' ? 'Your upcoming meetings will appear here' :
          activeTab === 'past' ? 'No past meetings found' :
          activeTab === 'accepted' ? 'No accepted meetings yet' :
          'No denied meetings yet'
        }}</p>
      </div>

      <div v-else class="booking-grid">
        <div v-for="mt in filteredMeetings" :key="mt.id" class="booking-card">
          <div class="booking-date-block">
            <span class="booking-day">{{ formatDay(mt.date) }}</span>
            <span class="booking-month">{{ formatMonth(mt.date) }}</span>
          </div>
          <div class="booking-middle">
            <p class="booking-pet-name">{{ mt.pet_name }}</p>
            <p class="booking-time">{{ formatTime(mt.time) }}</p>
          </div>
          <span class="status-badge" :class="statusClass(mt.confirmation)">
            {{ getStatusLabel(mt.confirmation) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'CalendarPage',
  props: {
    user: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      designUrl: '/background_designs/',
      meetings: [],
      userType: null,
      syncing: false,
      previousMeetingCount: 0,
      sidebarOpen: false,
      activeTab: 'upcoming',
      petsOpen: false,
    };
  },
  computed: {
    filteredMeetings() {
      const now = new Date();
      return this.meetings.filter(mt => {
        const mtDate = new Date(mt.date);
        if (this.activeTab === 'upcoming') return mtDate >= now;
        if (this.activeTab === 'past') return mtDate < now;
        if (this.activeTab === 'accepted') return mt.confirmation === 1;
        if (this.activeTab === 'denied') return mt.confirmation === 0;
        return true;
      });
    }
  },
  async mounted() {
    try {
      const { data: me } = await axios.get(`${backendUrl}/api/auth/me`, { withCredentials: true });
      this.userType = me.user_type;
    } catch (e) {
      console.error('Couldn\'t fetch user type', e);
      return;
    }
    await this.load();
    const hasSynced = localStorage.getItem('hasSyncedCalendar');
    if (!hasSynced && this.meetings.length > 0) {
      await this.syncToGoogle();
      localStorage.setItem('hasSyncedCalendar', 'true');
    }
    setInterval(this.load, 30000);
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    getStatusLabel(status) {
      if (status === 1) return 'Accepted';
      if (status === 0) return 'Pending';
      return 'Completed';
    },
    async load() {
      const url = backendUrl + (this.userType === 'Pet Lover' ? '/api/meetings/user' : '/api/meetings/requests');
      try {
        const { data } = await axios.get(url, { withCredentials: true });
        this.meetings = data;
        this.previousMeetingCount = data.length;
      } catch (e) {
        console.error('Failed to load meetings', e);
      }
    },
    formatTime(timeStr) {
      let [h, m] = timeStr.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
    },
    formatDay(dateStr) {
      return String(new Date(dateStr).getDate()).padStart(2, '0');
    },
    formatMonth(dateStr) {
      return new Date(dateStr).toLocaleString('default', { month: 'short' }).toUpperCase();
    },
    statusClass(status) {
      if (status === 1) return 'status-accepted';
      if (status === 0) return 'status-pending';
      return 'status-completed';
    },
    async syncToGoogle() {
      this.syncing = true;
      try {
        const { data: pets } = await axios.get(`${backendUrl}/api/pets/pets`, { withCredentials: true });
        const events = this.meetings.map(m => {
          const pet = pets.find(p => p.pet_name === m.pet_name);
          if (!pet) return null;
          return { pet_id: pet.id, date: m.date, time: m.time };
        }).filter(e => e !== null);
        await axios.post(`${backendUrl}/api/meetings/sync-calendar`, { calendarEvents: events }, { withCredentials: true });
      } catch (err) {
        console.error('Sync failed:', err);
        const msg = err.response?.data?.error || '';
        if (msg.includes('No Google tokens')) {
          window.location.href = `${backendUrl}/api/auth/google`;
        }
      } finally {
        this.syncing = false;
      }
    },
  },
};
</script>

<style scoped>
* { margin: 0; padding: 0; box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

.calendar-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px 0 24px;
}

.page-title { font-size: 26px; font-weight: 800; color: #1a1a1a; letter-spacing: -0.4px; margin: 0; }
.page-sub { font-size: 14px; color: #aaa; font-weight: 500; margin-top: 4px; }

.page-wrapper { max-width: 800px; margin: 0 auto; padding: 24px 20px 60px; }

.controls-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; gap: 12px; }

.tab-group { display: flex; background: #fff; border: 1px solid #f0e0ff; border-radius: 999px; padding: 4px; gap: 4px; }

.tab-btn { padding: 8px 20px; border-radius: 999px; border: none; background: none; font-size: 13px; font-weight: 700; color: #aaa; cursor: pointer; transition: all 0.18s ease; }
.tab-btn:hover { color: #4a1070; }
.tab-active { background: #eac7ff; color: #4a1070; }

.sync-btn { display: flex; align-items: center; gap: 6px; padding: 9px 20px; background: #ffec90; border: 1px solid #f6d860; border-radius: 999px; color: #92610a; font-size: 13px; font-weight: 700; cursor: pointer; transition: background 0.18s; white-space: nowrap; }
.sync-btn:hover { background: #ffe342; }
.sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.spinning { animation: spin 0.8s linear infinite; }

.booking-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }

.booking-card { background: #fff; border-radius: 20px; border: 1px solid #f0e0ff; padding: 18px 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 4px 16px rgba(186,113,255,0.07); transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s; }
.booking-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(186,113,255,0.13); }

.booking-date-block { flex-shrink: 0; width: 52px; height: 58px; background: linear-gradient(135deg, #eac7ff, #dda6fd); border-radius: 14px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; }
.booking-day { font-size: 20px; font-weight: 800; color: #4a1070; line-height: 1; }
.booking-month { font-size: 10px; font-weight: 700; color: #7a30a8; letter-spacing: 0.06em; }

.booking-middle { flex: 1; min-width: 0; }
.booking-pet-name { font-size: 15px; font-weight: 800; color: #1a1a1a; margin: 0 0 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.booking-time { font-size: 13px; color: #999; font-weight: 600; margin: 0; }

.status-badge { flex-shrink: 0; font-size: 12px; font-weight: 700; padding: 5px 13px; border-radius: 999px; letter-spacing: 0.02em; }
.status-accepted { background: #d4f5e2; color: #1a6b3c; }
.status-completed { background: #d4f5e2; color: #1a6b3c; }
.status-pending { background: #fffbe8; color: #92610a; border: 1px solid #f6d860; }

.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 10px; }
.empty-icon { font-size: 48px; opacity: 0.5; }
.empty-text { font-size: 17px; font-weight: 700; color: #333; margin: 0; }
.empty-sub { font-size: 13px; color: #bbb; margin: 0; text-align: center; }

@media (max-width: 600px) { .booking-grid { grid-template-columns: 1fr; } .page-wrapper { padding: 20px 14px 40px; } }
</style>